import { Tree as AntdTree } from "antd/es";
import { DataNode } from "antd/es/tree";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { CSSProperties, PureComponent, ReactNode } from "react";
import isEqual from "react-fast-compare";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";

declare let window: Window & { [key: string]: any };

const designTimeoptions = [
  {
    id: 1,
    parentId: null,
    label: "parent1"
  },
  {
    id: 2,
    parentId: 1,
    label: "children1"
  },
  {
    id: 3,
    parentId: 1,
    label: "children2"
  },
  {
    id: 4,
    parentId: 2,
    label: "children3"
  },
  {
    id: 5,
    parentId: 4,
    label: "children4"
  },
  {
    id: 6,
    parentId: null,
    label: "parent2"
  }
];

export interface KTreeProps {
  value?: string | number;
  options?: object[];
  datavaluefield?: string;
  datatextfield?: string;
  dataparentfield?: string;
  datacheckedfield?: string;
  disablecheckboxfield?: string;
  defaultCheckedKeys?: string; // comma separated ids
  className?: string;
  style?: CSSProperties;
  onSelect?: (value: number | string) => void;
  onChange?: (value: number | string) => void;
  onCheck?: () => void;
  checkable?: boolean;
}

interface TreeState {
  selectedKeys: (number | string)[];
  checkedKeys: (string | number)[];
}

class Tree extends PureComponent<KTreeProps, TreeState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`ktree_${this.uniqueKey}`);

  constructor(props: KTreeProps) {
    super(props);
    this.state = {
      selectedKeys: [],
      checkedKeys: []
    };
  }

  private getProps = () => {
    return _.omit(this.props, [
      "datatextfield",
      "datavaluefield",
      "dataparentfield",
      "datacheckedfield",
      "defaultChecked",
      "options",
      "checkable",
      "disablecheckboxfield",
      "checkedKeys",
      "onCheck"
    ]);
  };

  private nest = (
    items: object[],
    id: string | null = null,
    link: string,
    processedIds: Set<string> = new Set()
  ): DataNode[] => {
    let { datatextfield, datavaluefield, datacheckedfield, disablecheckboxfield } = this.props;

    if (window.kuika?.dashboardState === 1) {
      datatextfield = "label";
      datavaluefield = "id";
      datacheckedfield = "checked";
      disablecheckboxfield = "disableCheckbox";
    }

    return items
      .filter((item: any) => {
        if (processedIds.has(item[datavaluefield])) {
          return false;
        }

        if (id === null) {
          return !item[link] || item[link] === "null" || item[link] === "";
        }
        return item[link] === id;
      })
      .map((item: any) => {
        processedIds.add(item[datavaluefield]);

        return {
          ...item,
          children: this.nest(items, item[datavaluefield], link, processedIds),
          title: item[datatextfield],
          checkable: disablecheckboxfield ? Boolean(item[disablecheckboxfield]) == false : true,
          key: item[datavaluefield],
          checked: datacheckedfield ? Boolean(item[datacheckedfield]) : undefined
        };
      });
  };

  private getClassName = (): string => {
    let result = "";

    if (this.props.className) {
      result = `${this.props.className} `;
    }

    if (!this.uniqueKey) {
      return result;
    }

    result = `${result}tree_${this.uniqueKey.toString()}`;
    return result;
  };

  private getBorderRadius = (): string | null => {
    if (!this.props.style?.borderRadius) return null;

    return `border-radius: ${this.props.style?.borderRadius}px;`;
  };

  private getBorderTopLeftRadius = (): string | null => {
    if (!this.props.style?.borderTopLeftRadius) return null;

    return `border-top-left-radius: ${this.props.style?.borderTopLeftRadius}px;`;
  };

  private getBorderTopRightRadius = (): string | null => {
    if (!this.props.style?.borderTopRightRadius) return null;

    return `border-top-right-radius: ${this.props.style?.borderTopRightRadius}px;`;
  };

  private getBorderBottomLeftRadius = (): string | null => {
    if (!this.props.style?.borderBottomLeftRadius) return null;

    return `border-bottom-left-radius: ${this.props.style?.borderBottomLeftRadius}px;`;
  };

  private getBorderBottomRightRadius = (): string | null => {
    if (!this.props.style?.borderBottomRightRadius) return null;

    return `border-bottom-right-radius: ${this.props.style?.borderBottomRightRadius}px;`;
  };

  private getBorderColor = (): string | null => {
    if (!this.props.style?.borderColor) return null;

    return `border-color: ${this.props.style?.borderColor};`;
  };

  private getBorderWidth = (): string | null => {
    if (!this.props.style?.borderWidth) return null;

    if (typeof this.props.style?.borderWidth === "string") {
      return `border-width: ${this.props.style?.borderWidth};`;
    }

    if (typeof this.props.style?.borderWidth === "number") {
      return `border-width: ${this.props.style?.borderWidth}px;`;
    }

    return null;
  };

  private getBorderTopWidth = (): string | null => {
    if (!this.props.style?.borderTopWidth) return null;

    return `border-top-width: ${this.props.style?.borderTopWidth}px;`;
  };

  private getBorderRightWidth = (): string | null => {
    if (!this.props.style?.borderRightWidth) return null;

    return `border-right-width: ${this.props.style?.borderRightWidth}px;`;
  };

  private getBorderBottomWidth = (): string | null => {
    if (!this.props.style?.borderBottomWidth) return null;

    return `border-bottom-width: ${this.props.style?.borderBottomWidth}px;`;
  };

  private getBorderLeftWidth = (): string | null => {
    if (!this.props.style?.borderLeftWidth) return null;

    return `border-left-width: ${this.props.style?.borderLeftWidth}px;`;
  };

  private getBorderStyle = (): string | null => {
    if (!this.props.style?.borderStyle) return null;

    return `border-style: ${this.props.style?.borderStyle};`;
  };

  private getDynamicCss = (): string => {
    const className: string = this.getClassName();

    if (!className || className.length === 0) {
      return "";
    }

    let result = "";

    const borderColor: string | null = this.getBorderColor();
    const borderStyle: string | null = this.getBorderStyle();

    if (this.getIsAnyBorderRadiusSet()) {
      if (this.props.style?.borderRadius) {
        result += `.${className} { ${this.getBorderRadius()} }`;
      }

      if (this.props.style?.borderTopLeftRadius) {
        result += `.${className} { ${this.getBorderTopLeftRadius()} }`;
      }

      if (this.props.style?.borderTopRightRadius) {
        result += `.${className} { ${this.getBorderTopRightRadius()} }`;
      }

      if (this.props.style?.borderBottomLeftRadius) {
        result += `.${className} { ${this.getBorderBottomLeftRadius()} }`;
      }

      if (this.props.style?.borderBottomRightRadius) {
        result += `.${className} { ${this.getBorderBottomRightRadius()} }`;
      }
    }

    if (this.getIsAnyBorderWidthSet()) {
      if (this.props.style?.borderWidth) {
        result += `.${className} { ${this.getBorderWidth()} }`;
      }

      if (this.props.style?.borderTopWidth) {
        result += `.${className} { ${this.getBorderTopWidth()} }`;
      }

      if (this.props.style?.borderRightWidth) {
        result += `.${className} { ${this.getBorderRightWidth()} }`;
      }

      if (this.props.style?.borderBottomWidth) {
        result += `.${className} { ${this.getBorderBottomWidth()} }`;
      }

      if (this.props.style?.borderLeftWidth) {
        result += `.${className} { ${this.getBorderLeftWidth()} }`;
      }
    }

    if (borderColor) {
      result += `.${className} { ${borderColor} }`;
    }

    if (borderStyle) {
      result += `.${className} { ${borderStyle} }`;
    }

    return result;
  };

  private getStyle = (): CSSProperties => {
    const style: CSSProperties = { ...this.props.style };

    if (style.borderRadius) {
      delete style.borderRadius;
    }

    if (style.borderTopLeftRadius) {
      delete style.borderTopLeftRadius;
    }

    if (style.borderTopRightRadius) {
      delete style.borderTopRightRadius;
    }

    if (style.borderBottomLeftRadius) {
      delete style.borderBottomLeftRadius;
    }

    if (style.borderBottomRightRadius) {
      delete style.borderBottomRightRadius;
    }

    if (style.borderWidth) {
      delete style.borderWidth;
    }

    if (style.borderTopWidth) {
      delete style.borderTopWidth;
    }

    if (style.borderRightWidth) {
      delete style.borderRightWidth;
    }

    if (style.borderBottomWidth) {
      delete style.borderBottomWidth;
    }

    if (style.borderLeftWidth) {
      delete style.borderLeftWidth;
    }

    if (style.borderColor) {
      delete style.borderColor;
    }

    if (style.borderStyle) {
      delete style.borderStyle;
    }

    return style;
  };

  private getIsAnyBorderWidthSet = (): boolean => {
    return Boolean(
      this.props.style?.borderWidth ||
      this.props.style?.borderTopWidth ||
      this.props.style?.borderRightWidth ||
      this.props.style?.borderBottomWidth ||
      this.props.style?.borderLeftWidth
    );
  };

  private getIsAnyBorderRadiusSet = (): boolean => {
    return Boolean(
      this.props.style?.borderRadius ||
      this.props.style?.borderTopLeftRadius ||
      this.props.style?.borderTopRightRadius ||
      this.props.style?.borderBottomLeftRadius ||
      this.props.style?.borderBottomRightRadius
    );
  };

  componentDidMount(): void {
    if (!this.uniqueKey) return;
    // Prefer defaultChecked (comma separated ids) over datacheckedfield on initial mount
    const defaultKeys = this.getDefaultCheckedKeysFromProp();
    if (defaultKeys.length > 0) {
      this.setState({ checkedKeys: defaultKeys });
    } else {
      this.updateCheckedKeysFromOptions();
    }
    if (this.props.value != null && this.props.value !== "") {
      this.setState({ selectedKeys: [this.props.value] });
    }
    this.setDynamicStyle();
  }

  componentDidUpdate(prevProps: KTreeProps): void {
    if (this.props.defaultCheckedKeys !== prevProps.defaultCheckedKeys) {
      const defaultKeys = this.getDefaultCheckedKeysFromProp();
      this.setState({ checkedKeys: defaultKeys });
    } else if (
      (!this.props.defaultCheckedKeys || this.props.defaultCheckedKeys.length === 0) &&
      (this.props.options !== prevProps.options || this.props.datacheckedfield !== prevProps.datacheckedfield)
    ) {
      this.updateCheckedKeysFromOptions();
    }
    if (this.props.value !== prevProps.value) {
      if (this.props.value != null && this.props.value !== "") {
        this.setState({ selectedKeys: [this.props.value] });
      } else {
        this.setState({ selectedKeys: [] });
      }
    }
    if (!isEqual(prevProps, this.props)) {
      this.setDynamicStyle();
    }
  }

  componentWillUnmount(): void {
    this.style.removeStyle();
  }

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  private updateCheckedKeysFromOptions = (): void => {
    if (this.props.datacheckedfield && this.props.options) {
      const checkedKeys = this.props.options
        .filter((item) => item[this.props.datacheckedfield])
        .map((item) => item[this.props.datavaluefield]);
      this.setState({ checkedKeys });
    }
  };

  private getDefaultCheckedKeysFromProp = (): (string | number)[] => {
    const { defaultCheckedKeys, options, datavaluefield } = this.props;
    if (!defaultCheckedKeys) return [];
    const list = defaultCheckedKeys
      .split(",")
      .map((x) => x.trim())
      .filter((x) => x.length > 0);
    if (options && datavaluefield) {
      const sample = (options as any[]).find((o) => o && o[datavaluefield] !== undefined);
      if (sample) {
        const sampleVal = sample[datavaluefield];
        if (typeof sampleVal === "number") {
          return list.map((v) => Number(v)).filter((v) => !Number.isNaN(v));
        }
      }
    }
    return list;
  };

  private dedupeByValueField = (items: object[], valueField: string): object[] => {
    const seen: Set<string | number> = new Set<string | number>();
    const result: object[] = [];
    for (const item of items) {
      const record = item as Record<string, string | number | boolean | null | undefined>;
      const key = record[valueField];
      if (typeof key !== "string" && typeof key !== "number") {
        result.push(item);
        continue;
      }
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      result.push(item);
    }
    return result;
  };

  private getTreeData = (): DataNode[] => {
    let treeData: DataNode[] = [];
    let options = !_.isEmpty(this.props.options) ? [...this.props.options] : [];

    if (window.kuika && window.kuika?.dashboardState === 1) {
      options = designTimeoptions;
      return this.nest(options, null, "parentId");
    }

    const { datatextfield, datavaluefield, dataparentfield, disablecheckboxfield } = this.props;

    options.forEach((element) => {
      if (element[datavaluefield] == element[dataparentfield]) {
        element[dataparentfield] = null;
      }

      if (element[dataparentfield] == undefined || element[dataparentfield] == "") {
        element[dataparentfield] = null;
      }
    });

    if (datavaluefield) {
      options = this.dedupeByValueField(options, datavaluefield);
    }

    if (options && datatextfield && datavaluefield && dataparentfield) {
      treeData = this.nest(options, null, dataparentfield);
    }

    return treeData;
  };

  private onSelect = (keys: (string | number)[]) => {
    if (window.kuika?.dashboardState === 1 || !this.props.datavaluefield || !this.props.options) {
      return;
    }

    this.setState({ selectedKeys: keys }, () => {
      this.props.onChange?.(this.state.selectedKeys[0]);
      this.props.onSelect?.(this.state.selectedKeys[0]);
    });
  };

  private onCheck = (checkedKeys: (string | number)[]) => {
    if (window.kuika?.dashboardState === 1 || !this.props.datavaluefield || !this.props.options) {
      return;
    }

    this.setState({ checkedKeys }, () => {
      this.props.onCheck?.();
    });
  };

  getCheckedKeys = (): string => {
    return this.state.checkedKeys.join(",");
  };

  getSelectedKey = (): string | number | undefined => {
    return this.state.selectedKeys[0];
  };

  getSelectedData = (): object | undefined => {
    const selectedKey = this.state.selectedKeys[0];
    if (selectedKey == null || !this.props.options || !this.props.datavaluefield) {
      return undefined;
    }
    return this.props.options.find((item: any) => item[this.props.datavaluefield] == selectedKey);
  };

  render(): ReactNode {
    const { checkable } = this.props;

    return (
      <AntdTree
        {...this.getProps()}
        selectedKeys={this.state.selectedKeys}
        checkedKeys={this.state.checkedKeys}
        defaultCheckedKeys={this.getDefaultCheckedKeysFromProp()}
        className={this.getClassName()}
        treeData={this.getTreeData()}
        style={this.getStyle()}
        onSelect={this.onSelect}
        onCheck={this.onCheck}
        checkable={checkable}
      />
    );
  }
}

const tree = withCommonEvents(Tree);
export { tree as Tree };
